/**
 * Auto layout using dagre for directed graph layout
 *
 * Uses the new clean architecture with connections.
 */

import * as Dagre from '@dagrejs/dagre';
import { Position } from '@/runtime/RuntimeStore';
import type { GraphDefinition } from '@/graph/GraphDefinition';

export interface LayoutOptions {
  direction?: 'TB' | 'LR' | 'BT' | 'RL';
  nodeWidth?: number;
  nodeHeight?: number;
  rankSep?: number;
  nodeSep?: number;
}

const defaultOptions: Required<LayoutOptions> = {
  direction: 'LR',  // Left to right - typical for audio signal flow
  nodeWidth: 220,
  nodeHeight: 150,
  rankSep: 100,    // Horizontal spacing between ranks
  nodeSep: 60,     // Vertical spacing between nodes
};

/**
 * Auto-layout a graph using dagre (directed acyclic graph layout).
 * Arranges nodes left-to-right based on internal connections.
 */
export function autoLayout(
  graph: GraphDefinition,
  nodeIds: string[],
  options: LayoutOptions = {}
): Record<string, Position> {
  const opts = { ...defaultOptions, ...options };

  const g = new Dagre.graphlib.Graph();

  g.setGraph({
    rankdir: opts.direction,
    ranksep: opts.rankSep,
    nodesep: opts.nodeSep,
  });

  g.setDefaultEdgeLabel(() => ({}));

  // Add nodes
  for (const nodeId of nodeIds) {
    g.setNode(nodeId, {
      width: opts.nodeWidth,
      height: opts.nodeHeight,
    });
  }

  // Add edges from internal connections
  for (const conn of graph.connections) {
    const [sourceNodeId] = conn.source.split('.');
    const [targetNodeId] = conn.target.split('.');
    g.setEdge(sourceNodeId, targetNodeId);
  }

  // Run layout
  Dagre.layout(g);

  // Extract positions (dagre gives center, xyflow uses top-left)
  const positions: Record<string, Position> = {};

  for (const nodeId of nodeIds) {
    const node = g.node(nodeId);
    if (node) {
      positions[nodeId] = {
        x: node.x - opts.nodeWidth / 2,
        y: node.y - opts.nodeHeight / 2,
      };
    }
  }

  return positions;
}

/**
 * Auto-layout nodes based on explicit edges (for root view with InstanceConnections).
 * Arranges nodes left-to-right following source->target relationships.
 */
export function autoLayoutFromEdges(
  nodeIds: string[],
  edges: Array<{ source: string; target: string }>,
  options: LayoutOptions = {}
): Record<string, Position> {
  const opts = { ...defaultOptions, ...options };

  const g = new Dagre.graphlib.Graph();

  g.setGraph({
    rankdir: opts.direction,
    ranksep: opts.rankSep,
    nodesep: opts.nodeSep,
  });

  g.setDefaultEdgeLabel(() => ({}));

  // Add nodes
  for (const nodeId of nodeIds) {
    g.setNode(nodeId, {
      width: opts.nodeWidth,
      height: opts.nodeHeight,
    });
  }

  // Add edges
  for (const edge of edges) {
    // Only add edge if both nodes exist
    if (nodeIds.includes(edge.source) && nodeIds.includes(edge.target)) {
      g.setEdge(edge.source, edge.target);
    }
  }

  // Run layout
  Dagre.layout(g);

  // Extract positions (dagre gives center, xyflow uses top-left)
  const positions: Record<string, Position> = {};

  for (const nodeId of nodeIds) {
    const node = g.node(nodeId);
    if (node) {
      positions[nodeId] = {
        x: node.x - opts.nodeWidth / 2,
        y: node.y - opts.nodeHeight / 2,
      };
    }
  }

  return positions;
}
